import type { CSSProperties } from 'react';
import styled from 'styled-components';

interface FormInputProps {
  label: string;
  type?: 'text' | 'tel' | 'email' | 'password';
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  subLabel?: string;
  required?: boolean;
  hideRequiredLabel?: boolean;
  requiredAsterisk?: boolean;
  largeLabel?: boolean;
  alternate?: boolean;
  greyBackground?: boolean;
  inputStyle?: CSSProperties;
  error?: boolean;
  errorMessage?: string;
  autoComplete?: string;
}

export function FormInput({
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  subLabel,
  required = false,
  hideRequiredLabel = false,
  requiredAsterisk = false,
  largeLabel = false,
  alternate = false,
  greyBackground = false,
  inputStyle,
  error = false,
  errorMessage,
  autoComplete,
}: FormInputProps) {
  return (
    <FormField>
      <Label $alternate={alternate} $error={error} $largeLabel={largeLabel}>
        {label}
        {required && requiredAsterisk && <Asterisk> *</Asterisk>}
        {required &&
          !requiredAsterisk &&
          !hideRequiredLabel &&
          /* Translation Needed */ ' (required)'}
      </Label>
      {subLabel && <SubLabel>{subLabel}</SubLabel>}
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        autoComplete={autoComplete}
        $alternate={alternate}
        $error={error}
        $greyBackground={greyBackground}
        style={inputStyle}
      />
      {error && errorMessage && <ErrorText>{errorMessage}</ErrorText>}
    </FormField>
  );
}

export const FormFieldLabel = styled.label`
  color: var(--black);
  display: block;
  font-family: var(--raleway);
  font-size: 15px;
  font-weight: 300;
  letter-spacing: -0.25px;
  margin-bottom: 12px;
`;

const FormField = styled.div`
  margin-bottom: 16px;
`;

const Label = styled(FormFieldLabel)<{
  $alternate: boolean;
  $error: boolean;
  $largeLabel: boolean;
}>`
  ${({ $alternate, $error, $largeLabel }) => {
    if ($largeLabel) {
      return `
        color: ${$error ? 'var(--wbs-red)' : 'var(--black)'};
        font-size: 14px;
        font-weight: 500;
      `;
    }
    if ($error) {
      return `
        color: var(--wbs-red);
        font-size: 12px;
      `;
    }
    if (!$alternate) {
      return `
        color: var(--grey-700);
        font-size: 12px;
      `;
    }
    return '';
  }}
`;

const Asterisk = styled.span`
  color: var(--wbs-red);
  font-size: 22px;
  font-weight: 400;
`;

const SubLabel = styled.div`
  color: var(--grey-500);
  font-family: var(--raleway);
  font-size: 13px;
  font-weight: 300;
  letter-spacing: -0.2px;
  margin-bottom: 16px;
`;

const Input = styled.input<{
  $alternate: boolean;
  $error: boolean;
  $greyBackground: boolean;
}>`
  background: ${(props) =>
    props.$greyBackground ? 'var(--grey-75)' : 'var(--white)'};
  border: ${(props) => {
    if (props.$error) return '1px solid var(--wbs-red)';
    return props.$alternate ? 'none' : '1px solid var(--grey-300)';
  }};
  border-radius: 4px;
  box-shadow: ${(props) =>
    props.$alternate ? 'var(--shadow-inset-md)' : 'var(--shadow-inset-sm)'};
  box-sizing: border-box;
  color: var(--grey-900);
  font-family: ${(props) =>
    props.$alternate ? 'var(--raleway)' : 'var(--open-sans)'};
  font-size: ${(props) => (props.$alternate ? '13px' : '12px')};
  padding: 7px 12px;
  transition:
    background-color 0.2s,
    border-color 0.2s;
  width: 100%;

  &::placeholder {
    color: var(--grey-600);
    opacity: 1;
  }

  &:focus {
    background-color: var(--grey-100);
    border-color: var(--black);
    outline: none;
  }
`;

const ErrorText = styled.div`
  color: var(--wbs-red);
  font-family: var(--raleway);
  font-size: 12px;
  margin-top: 6px;
`;
